import { formatMoney, getMinifiedLocaleCurrencyStringFromMoneyAmount } from 'utils'
import { calculateMonthlyEmployeeOutOfPocketContribution } from 'utils/quote_calculations'
import { type QuotingQuoteResult } from 'utils/web_api.schemas'

import { type BucketData, type EmployeeWithCostData } from './types'
import { colors } from '../../../../../config/tailwind/colors'

export const BUCKET_COUNT = 8

// Color constants for cost impact charts
export const GREEN_COLOR = colors.primitive.green[300]
export const DARK_GREEN_COLOR = colors.primitive.green[400]
export const RED_COLOR = colors.primitive.red[400]
export const DARK_RED_COLOR = colors.primitive.red[500]

export const moneyWithoutCents = (value: number) => (
  getMinifiedLocaleCurrencyStringFromMoneyAmount({
    value: value.toString(),
    currency: 'USD',
  })
)

/**
 * Calculates the employee cost difference (current cost - new cost)
 * Positive = employee saves money, Negative = employee pays more
 */
export const getEmployeeCostData = (
  employee: QuotingQuoteResult,
  employerEmployeeContribution: number,
  employerSpouseContribution: number,
  employerChildContribution: number
): EmployeeWithCostData | null => {
  if (employee.employee_medical_contribution == null) return null

  const newOutOfPocket = calculateMonthlyEmployeeOutOfPocketContribution(employee, {
    employerEmployeeContribution,
    employerSpouseContribution,
    employerChildContribution,
  })

  // Positive = savings (current > new), Negative = paying more (current < new)
  return {
    employee,
    currentCost: employee.employee_medical_contribution,
    newCost: newOutOfPocket,
    difference: employee.employee_medical_contribution - newOutOfPocket,
  }
}

/**
 * Creates histogram buckets from an array of employee cost data
 */
export const createBuckets = (employeeCostData: EmployeeWithCostData[], bucketCount: number): BucketData[] => {
  if (employeeCostData.length === 0) return []

  const values = employeeCostData.map(e => e.difference)
  const min = Math.min(...values)
  const max = Math.max(...values)

  // Handle edge case where all values are the same
  if (min === max) {
    return [{
      range: formatMoney(min),
      count: employeeCostData.length,
      min,
      max,
      isSavings: min >= 0,
      employees: employeeCostData,
    }]
  }

  // Calculate nice rounded bucket width
  const rawBucketWidth = (max - min) / bucketCount
  // Round to a nice number (nearest 25, 50, 100, etc.)
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawBucketWidth)))
  const normalized = rawBucketWidth / magnitude
  let niceWidth: number
  if (normalized <= 1) niceWidth = magnitude
  else if (normalized <= 2) niceWidth = 2 * magnitude
  else if (normalized <= 2.5) niceWidth = 2.5 * magnitude
  else if (normalized <= 5) niceWidth = 5 * magnitude
  else niceWidth = 10 * magnitude

  // Ensure minimum bucket width of $25
  niceWidth = Math.max(niceWidth, 25)

  // Calculate rounded min and max
  const roundedMin = Math.floor(min / niceWidth) * niceWidth
  const roundedMax = Math.ceil(max / niceWidth) * niceWidth
  const actualBucketCount = Math.ceil((roundedMax - roundedMin) / niceWidth)

  // Create buckets
  const buckets: BucketData[] = []
  for (let i = 0; i < actualBucketCount; i++) {
    const bucketMin = roundedMin + i * niceWidth
    const bucketMax = bucketMin + niceWidth

    const bucketEmployees = employeeCostData.filter(e => {
      if (i === actualBucketCount - 1) {
        // Last bucket includes the max value
        return e.difference >= bucketMin && e.difference <= bucketMax
      }
      return e.difference >= bucketMin && e.difference < bucketMax
    })

    // Determine if this bucket represents savings (midpoint >= 0)
    const midpoint = (bucketMin + bucketMax) / 2
    const isSavings = midpoint >= 0

    buckets.push({
      range: [Math.abs(bucketMin), Math.abs(bucketMax)].sort((a, b) => a - b).map(moneyWithoutCents).join(' to '),
      count: bucketEmployees.length,
      min: bucketMin,
      max: bucketMax,
      isSavings,
      employees: bucketEmployees,
    })
  }

  return buckets
}
